import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import Switch from 'components/ui/Switch';
import COLOR from 'constants/color';
import { Apps } from 'assets/icons/revamp';
import Card from 'components/ui/Card';
import { t } from 'services/i18n.service';
import {
  updateMoreOptionAllowedApps,
  updateMoreOptionIsUseApplicationsChecked
} from 'store/reducer/modal/slice';
import { moreOptionsApplicationsSelector } from 'store/reducer/setting/selectors';
import { AutoCompletionTags } from 'components/common/AutoCompletionTags';
import {
  DEFAULT_MAC_APPS_IF_ALLOWED_URLS_SET,
  DEFAULT_WINDOWS_APPS_IF_ALLOWED_URLS_SET,
  PLATFORMS
} from 'constants/general';

const Applications: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    suggestions,
    platform,
    activity,
    isUseApplicationsChecked,
    isUseWebsiteChecked
  } = useAppSelectorShallow(moreOptionsApplicationsSelector);

  const allowedApps = useMemo(
    () => activity?.allowed_apps ?? [],
    [activity?.allowed_apps]
  );

  const applicationTags = useMemo(
    () =>
      allowedApps.map((app) => ({
        id: app,
        text: app
      })),
    [allowedApps]
  );

  const suggestionTags = useMemo(
    () =>
      suggestions?.map((item: string) => ({
        id: item.toString().toLowerCase(),
        text: item.toString()
      })),
    [suggestions]
  );

  useEffect(() => {
    dispatch(
      updateMoreOptionIsUseApplicationsChecked(Boolean(allowedApps.length))
    );
  }, [allowedApps.length, dispatch]);

  useEffect(() => {
    if (isUseWebsiteChecked && !allowedApps.length) {
      dispatch(updateMoreOptionIsUseApplicationsChecked(true));
      const defaultAllowedApps =
        platform === PLATFORMS.WIN
          ? DEFAULT_WINDOWS_APPS_IF_ALLOWED_URLS_SET
          : DEFAULT_MAC_APPS_IF_ALLOWED_URLS_SET;
      dispatch(updateMoreOptionAllowedApps(defaultAllowedApps));
    }
  }, [isUseWebsiteChecked, allowedApps.length, dispatch, platform]);

  const handleToggleApps = (checked: boolean) => {
    dispatch(updateMoreOptionIsUseApplicationsChecked(checked));
    if (!checked) {
      dispatch(updateMoreOptionAllowedApps([]));
    }
  };

  return (
    <Card className='flex flex-col ~gap-3/4'>
      <div className='flex flex-col ~gap-1/2'>
        <div className='flex items-center ~gap-3/6'>
          <h3 className='~text-xs/base font-semibold flex items-center ~gap-1/2'>
            <Apps styles='~w-4/5 ~h-4/5' fill={COLOR.PRIMARY.LIGHT_900} />
            {t('tabs_revamp.allowed_apps_tab.allow_apps')}
          </h3>
          <Switch
            checked={isUseApplicationsChecked}
            onChange={handleToggleApps}
            size='medium'
            variant='default'
          />
        </div>
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400 ~ml-0/0'>
          {t('tabs_revamp.allowed_apps_tab.allow_apps_description')}
        </p>
      </div>

      {isUseApplicationsChecked && (
        <div className='flex flex-col ~gap-3/4'>
          <AutoCompletionTags
            tags={applicationTags}
            suggestions={suggestionTags}
            setTags={(apps) =>
              dispatch(updateMoreOptionAllowedApps(apps.map((app) => app.text)))
            }
            handleAddTag={(app) => {
              const alreadyExists = applicationTags.some(
                (existing) =>
                  existing.text.toLowerCase() === app.text.toLowerCase()
              );
              if (alreadyExists) {
                return;
              }

              if (platform === PLATFORMS.MOB) {
                const isInstalledApp = suggestions.some(
                  (suggestion) =>
                    suggestion.toLowerCase() === app.text.toLowerCase()
                );
                if (!isInstalledApp) {
                  return;
                }
              }

              dispatch(updateMoreOptionAllowedApps([...allowedApps, app.text]));
            }}
            placeholder={t('press_enter_to_add_new_app')}
          />
        </div>
      )}
    </Card>
  );
};

export default Applications;
